// ============================================================
// useCombatantSync - Firestore sync for the unified roster.
//
// Replaces useTargetSync. The important difference is that a combatant
// has TWO halves with wildly different write frequencies:
//
//   state  - every wound, condition and shot        (350ms debounce)
//   sheet  - only when someone deliberately edits   (700ms debounce)
//
// They are separate Firestore FIELDS, written with merge:true, so a
// wound tick never rewrites the fat sheet JSON and the two can land
// concurrently without clobbering each other.
//
// ECHO PREVENTION USES TWO KEYS PER COMBATANT. Sharing one key per
// document produces a write storm the first time a wound tick and a
// sheet edit interleave: each write invalidates the other's cache
// entry, so both keep re-firing. See dev-docs/gotchas.md.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { CollectionRead } from '../backend/types';
import type { Combatant, CombatState } from '../engine/roster/types';
import { combinePatches, statePatch, type StatePatch } from '../engine/roster/statePatch';
import { stateContent } from '../engine/roster/rev';
import { combatantToDoc } from '../engine/roster/serialize';
import useEchoPrevention from './useEchoPrevention';
import { changedFields, isWriteTraceOn, noteRosterWrite } from '../diagnostics/writeTrace';
import type { EchoPrevention } from './useEchoPrevention';
import { applyRead, rosterAuditor, sheetKey, stateKey } from './combatantSyncRead';

/** The document's cold half, in the exact shape parsedCombatants reports. */
function coldFields(d: ReturnType<typeof combatantToDoc>): Record<string, unknown> {
  return {
    name: d.name,
    kind: d.kind,
    order: d.order,
    sheet: d.sheet,
    overrides: d.overrides,
    loadoutRef: d.loadoutRef ?? null,
    slotOrder: d.slotOrder ?? null,
    schemaVersion: d.schemaVersion,
  };
}

const STATE_DEBOUNCE_MS = 350;
const SHEET_DEBOUNCE_MS = 700;
/** How often the roster audit re-reads the server (v8.362.1). */
export const ROSTER_AUDIT_MS = 60000;
export { STUB_AGE_MS } from './combatantSyncRead';

type Timers = Record<string, ReturnType<typeof setTimeout>>;

interface CombatantSyncMp {
  // `CollectionRead` - the backend port's own type. A listener hands over the
  // documents and the two answers about them together, so a consumer cannot
  // take one without the other.
  onCombatantsUpdate: (sessionId: string, cb: (read: CollectionRead<Record<string, unknown>>) => void) => () => void;
  // `stillWanted` is asked before every attempt, retries included (see multiplayer.ts).
  // `mustExist`: the document existed (seen whole here), so the half is an update that never re-creates it.
  setCombatantState: (
    s: string,
    id: string,
    state: Record<string, unknown>,
    p: string | null,
    stillWanted?: () => boolean,
    mustExist?: () => boolean,
  ) => Promise<void>;
  /** The state's changed keys only, onto a document that exists (plan state-field-merge); absent in older mocks. */
  mergeCombatantState?: (
    s: string,
    id: string,
    patch: StatePatch,
    p: string | null,
    stillWanted?: () => boolean,
  ) => Promise<void>;
  setCombatantSheet: (
    s: string,
    id: string,
    payload: Record<string, unknown>,
    p: string | null,
    stillWanted?: () => boolean,
    mustExist?: () => boolean,
  ) => Promise<void>;
  deleteCombatant: (s: string, id: string) => Promise<void>;
  batchWriteCombatants: (s: string, payloads: { id: string; doc: never }[]) => Promise<void>;
  /** The audit's one read of the server (v8.362.1). Optional: a caller without it gets no audit. */
  listCombatants?: (s: string) => Promise<{ id: string; data: Record<string, unknown> }[]>;
  /** Told after a reconnect - the audit runs at once. */
  onReconnect?: (listener: () => void) => () => void;
}

interface Params {
  sessionId: string | null;
  playerId: string | null;
  isGm: boolean;
  roster: Combatant[];
  setRoster: (updater: (prev: Combatant[]) => Combatant[]) => void;
  /**
   * When false the listener WRITES and MIGRATES but does not drive local
   * state - the legacy targets/* listener is still authoritative.
   *
   * Two listeners both calling setRoster gives the roster two masters and
   * they race, which is why the legacy listener is off whenever this is
   * on (see useFirestoreSync). Default since v7.3.0.
   */
  authoritative?: boolean;
  mp: CombatantSyncMp;
  onSyncError?: (message: string) => void;
}

/** Log a failed write and tell the user, once per failure. */
const failWith = (log: string, message: string, onSyncError: Params['onSyncError']) => (e: unknown) => {
  console.error(log, e);
  onSyncError?.(message);
};

/**
 * Send after `ms`, replacing any send still waiting for this id. The timer
 * entry is deleted only when the write SETTLES - the guard must hold until
 * the write lands, not until the timer fires: a snapshot arriving in
 * between still carries the old value, and accepting it would undo what
 * we are mid-way through writing (exactly how an undo got reverted).
 */
function debounceSend(
  timers: MutableRefObject<Timers>,
  id: string,
  ms: number,
  send: () => Promise<void>,
  onFail: (e: unknown) => void,
): void {
  clearTimeout(timers.current[id]);
  timers.current[id] = setTimeout(() => {
    send()
      .catch(onFail)
      .finally(() => {
        delete timers.current[id];
      });
  }, ms);
}

/** Every ref the sync keeps across renders; `rosterRef` is refreshed each render for the listener. */
function useSyncRefs(roster: Combatant[]) {
  // Each one's meaning is on `CombatantSyncRefs` (combatantSyncRead), its one documentation; `stateTimers`, the
  // state half's debounce timers by combatant, is the writers' own and not the listener's.
  const refs = {
    stateTimers: useRef<Timers>({}),
    // The state's changes waiting in the debounce, by combatant: null for "send it whole" (plan state-field-merge).
    statePatches: useRef<Record<string, StatePatch | null>>({}),
    sheetTimers: useRef<Timers>({}),
    initialSyncDone: useRef(false),
    confirmedIds: useRef<Set<string>>(new Set()),
    sheetSeenRef: useRef<Set<string>>(new Set()),
    rosterRef: useRef(roster),
    hydratedRef: useRef(false),
    removedRef: useRef<Set<string>>(new Set()),
    heardGoneRef: useRef<Set<string>>(new Set()),
    resentRef: useRef<Map<string, string>>(new Map()),
    streamSeq: useRef(0),
    goneAt: useRef<Map<string, number>>(new Map()),
  };
  refs.rosterRef.current = roster;
  return refs;
}

type SyncRefs = ReturnType<typeof useSyncRefs>;

/**
 * Does this screen still hold the combatant? Asked before every attempt of a
 * write to it, so a retry never re-creates someone deleted meanwhile.
 */
const holds = (rosterRef: SyncRefs['rosterRef'], id: string) => () => rosterRef.current.some((c) => c.id === id);

/** Whether a view has shown this combatant whole here - its document existed, so a half-write must not create it. */
const seenWhole = (sheetSeenRef: SyncRefs['sheetSeenRef'], id: string) => () => sheetSeenRef.current.has(id);

/**
 * Note an outgoing write and what it changes against what was last sent or
 * received - for the sandbox's write trace; nothing anywhere else.
 */
function traceSend(id: string, kind: string, before: string, now: unknown): void {
  if (isWriteTraceOn()) noteRosterWrite(id, kind, changedFields(before, now));
}

/**
 * What this edit adds to the combatant's waiting send: its changed keys against the copy last sent or received
 * (`base`, the echo cache's content), combined with what an earlier edit inside the debounce changed - or null, "send
 * it whole", with no base (a fresh add, a re-send, which clears it on purpose) or once anything waiting is whole.
 */
function queuePatch(patches: Record<string, StatePatch | null>, id: string, base: string, state: CombatState): void {
  const patch = base ? statePatch(JSON.parse(base), state as unknown as Record<string, unknown>) : null;
  const waiting = id in patches ? patches[id] : undefined;
  patches[id] = waiting === undefined ? patch : waiting && patch ? combinePatches(waiting, patch) : null;
}

/** The waiting send of one combatant, taken: its patch, or null to send it whole. */
function takePatch(patches: Record<string, StatePatch | null>, id: string): StatePatch | null {
  const patch = patches[id] ?? null;
  delete patches[id];
  return patch;
}

/**
 * The state half's writer. Compares CONTENT, not the clock: rev changes
 * on every write, so including it would make every state look dirty
 * forever. The clock is sent VERBATIM - it advanced when the state was
 * mutated, and bumping again here made every send look newer than
 * everyone else's (the v7.5.0 bug). FIELD BY FIELD once the document has
 * been seen whole (plan state-field-merge, 2026-10-24): only the keys
 * this screen changed, so a rival edit of other keys survives.
 */
function useStateWriter({ sessionId, playerId, mp, onSyncError }: Params, echo: EchoPrevention, refs: SyncRefs) {
  return useCallback(
    (c: Combatant) => {
      if (!sessionId) return;
      const base = echo.getCache(stateKey(c.id));
      if (!echo.shouldWrite(stateKey(c.id), stateContent(c.state))) return;
      traceSend(c.id, 'send-state', base, stateContent(c.state));
      queuePatch(refs.statePatches.current, c.id, base, c.state);
      const state = c.state as unknown as Record<string, unknown>;
      const stillHeld = holds(refs.rosterRef, c.id);
      const whole = seenWhole(refs.sheetSeenRef, c.id);
      const send = () => {
        const patch = takePatch(refs.statePatches.current, c.id);
        if (patch && mp.mergeCombatantState && whole())
          return mp.mergeCombatantState(sessionId, c.id, patch, playerId, stillHeld);
        return mp.setCombatantState(sessionId, c.id, state, playerId, stillHeld, whole);
      };
      const fail = failWith(
        '[CombatantSync] state write failed:',
        'Combat state sync failed - other players may not see this.',
        onSyncError,
      );
      debounceSend(refs.stateTimers, c.id, STATE_DEBOUNCE_MS, send, fail);
    },
    [sessionId, playerId, mp, echo, onSyncError, refs],
  );
}

/**
 * The cold half's writer. The clock rides the payload but stays OUT of
 * the compared fields: the echo cache compares content, and a clock in it
 * would make every receive-bump look like a fresh edit.
 */
function useSheetWriter({ sessionId, playerId, mp, onSyncError }: Params, echo: EchoPrevention, refs: SyncRefs) {
  return useCallback(
    (c: Combatant) => {
      if (!sessionId) return;
      const cold = coldFields(combatantToDoc(c, playerId));
      const before = isWriteTraceOn() ? echo.getCache(sheetKey(c.id)) : '';
      if (!echo.shouldWrite(sheetKey(c.id), cold)) return;
      traceSend(c.id, 'send-sheet', before, cold);
      const payload = { ...cold, sheetRev: c.sheetRev ?? 0 };
      const send = () =>
        mp.setCombatantSheet(
          sessionId,
          c.id,
          payload,
          playerId,
          holds(refs.rosterRef, c.id),
          seenWhole(refs.sheetSeenRef, c.id),
        );
      debounceSend(refs.sheetTimers, c.id, SHEET_DEBOUNCE_MS, send, sheetFailed(onSyncError));
    },
    [sessionId, playerId, mp, echo, onSyncError, refs.sheetTimers, refs.rosterRef, refs.sheetSeenRef],
  );
}

const sheetFailed = (onSyncError: Params['onSyncError']) =>
  failWith(
    '[CombatantSync] sheet write failed:',
    'Character sync failed - other players may not see this edit.',
    onSyncError,
  );

/**
 * Send this screen's sheet again, AT ONCE (v8.398.9): the server holds an
 * older one - a stale write from another screen replaced ours. Not through
 * the debounced writer: its pending timer would make the listener refuse
 * every sheet for 700 ms, so a real remote edit landing then was ignored
 * and our re-send overwrote it. Sent at once, whichever write the server
 * applies last comes back to every screen, and they converge.
 */
function useSheetResend(params: Params, echo: EchoPrevention, refs: SyncRefs) {
  // Through a ref, as useResend: the listener keeps the first render's
  // context for as long as it is subscribed.
  const latest = useRef(params);
  latest.current = params;
  return useCallback(
    (c: Combatant) => {
      const { sessionId, playerId, mp, onSyncError } = latest.current;
      if (!sessionId) return;
      const cold = coldFields(combatantToDoc(c, playerId));
      echo.setCache(sheetKey(c.id), cold);
      traceSend(c.id, 'resend-sheet', '', cold);
      const payload = { ...cold, sheetRev: c.sheetRev ?? 0 };
      mp.setCombatantSheet(
        sessionId,
        c.id,
        payload,
        playerId,
        holds(refs.rosterRef, c.id),
        seenWhole(refs.sheetSeenRef, c.id),
      ).catch(sheetFailed(onSyncError));
    },
    [echo, refs],
  );
}

/** Push the whole roster at once (migration, session import, GM bootstrap), caching both halves as written. */
function useBatchWriter({ sessionId, playerId, mp, onSyncError }: Params, echo: EchoPrevention) {
  return useCallback(
    (combatants: Combatant[]) => {
      if (!sessionId) return;
      for (const c of combatants) {
        echo.setCache(stateKey(c.id), stateContent(c.state));
        echo.setCache(sheetKey(c.id), coldFields(combatantToDoc(c, playerId)));
      }
      const payloads = combatants.map((c) => ({ id: c.id, doc: combatantToDoc(c, playerId) as never }));
      mp.batchWriteCombatants(sessionId, payloads).catch(
        failWith('[CombatantSync] batch write failed:', 'Roster sync failed.', onSyncError),
      );
    },
    [sessionId, playerId, mp, echo, onSyncError],
  );
}

/**
 * Remove a combatant: drop its pending writes and cache, mark it (see
 * `removedRef`), then delete it. Returns whether THIS SCREEN took them off.
 *
 * A DELETION THIS SCREEN ONLY HEARD IS NOT SENT BACK (2026-10-06): it
 * returns false and deletes nothing, and the caller leaves the token alone
 * too. Every client's departure effect calls this for whoever left its
 * roster; for a deletion the GM made that was a second, late delete, and
 * after a scene cut struck a traveller and staged them again it landed on
 * the RE-CREATED document. The two notes below are what that echo cost
 * before; they still hold for a departure made here.
 *
 * The mark is ALSO LIFTED WHEN THE DELETE IS ANSWERED (v8.361.3). Every
 * client's departure effect calls this for whoever left its roster - a
 * player, for a deletion the GM made - and by then the delete's snapshot
 * has been and gone. Nothing lifted the mark, so once the GM re-created
 * the document (a recall, then a deploy) that client ignored it until a
 * reload. Once the server has answered, the delete has landed. A refused
 * delete lifts it too - hiding a combatant that exists is the worse mistake.
 *
 * THE CONFIRMATION GOES WITH THE DOCUMENT (v8.400.13). Kept, it made the
 * same person brought back later (a replace, a deploy from a room) look
 * deleted elsewhere to the first complete view that did not list them yet
 * - their write is debounced, a departed's delete is not - and the merge
 * pruned them: a stand-in vanished from the map.
 */
function useRemoveCombatant({ sessionId, mp }: Params, echo: EchoPrevention, refs: SyncRefs) {
  const { removedRef, heardGoneRef, confirmedIds, sheetSeenRef, stateTimers, sheetTimers } = refs;
  return useCallback(
    (id: string): boolean => {
      confirmedIds.current.delete(id);
      // Gone from here: a later re-add of the same id is fresh again, its state half usable before its sheet.
      sheetSeenRef.current.delete(id);
      clearTimeout(stateTimers.current[id]);
      clearTimeout(sheetTimers.current[id]);
      delete stateTimers.current[id];
      delete sheetTimers.current[id];
      echo.deleteCache(stateKey(id));
      echo.deleteCache(sheetKey(id));
      // Heard, not made: the delete is somebody else's and has already landed. No mark
      // either - nothing of ours is in flight for a later view to be hidden behind.
      if (heardGoneRef.current.delete(id)) return false;
      removedRef.current.add(id);
      if (!sessionId) return true;
      const lift = () => removedRef.current.delete(id);
      mp.deleteCombatant(sessionId, id).then(lift, lift);
      return true;
    },
    [sessionId, mp, echo, removedRef, heardGoneRef, confirmedIds, sheetSeenRef, stateTimers, sheetTimers],
  );
}

/**
 * Everything is scoped to ONE session. Joining another must reset it all:
 * an echo cache carried across says "already written" about documents the
 * new session has never seen, so they are never sent and silently go
 * missing there. Runs before the listener effect, which it must.
 */
function useSessionReset(
  sessionId: string | null,
  isGm: boolean,
  echo: EchoPrevention,
  refs: SyncRefs,
  setHydrated: (h: boolean) => void,
) {
  useEffect(() => {
    echo.clearCache();
    refs.confirmedIds.current = new Set();
    refs.sheetSeenRef.current = new Set();
    refs.heardGoneRef.current = new Set();
    refs.resentRef.current = new Map();
    refs.goneAt.current = new Map();
    refs.initialSyncDone.current = false;
    refs.hydratedRef.current = false;
    setHydrated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isGm]);
}

/**
 * Send this screen's state again: forget that it was sent, then send it -
 * the server holds an older one (v8.393.9). Stable across renders, and
 * always the writer of the latest render, for a listener subscribed long
 * before it.
 */
function useResend(echo: EchoPrevention, writeState: (c: Combatant) => void) {
  const writeRef = useRef(writeState);
  writeRef.current = writeState;
  return useCallback(
    (local: Combatant) => {
      echo.deleteCache(stateKey(local.id));
      writeRef.current(local);
    },
    [echo],
  );
}

/**
 * The listener, and the roster audit beside it (v8.362.1): once a minute,
 * and at once after a reconnect, the client reads the collection itself
 * and applies it as a complete view. Both go through `applyRead`.
 */
function useRosterListener(
  params: Params,
  echo: EchoPrevention,
  refs: SyncRefs,
  setHydrated: (h: boolean) => void,
  writeState: (c: Combatant) => void,
) {
  const { sessionId, mp, authoritative, isGm, setRoster } = params;
  const resendState = useResend(echo, writeState);
  const resendSheet = useSheetResend(params, echo, refs);
  useEffect(() => {
    if (!sessionId) return;
    refs.initialSyncDone.current = false;
    const ctx = { ...refs, authoritative, isGm, echo, setRoster, setHydrated, resendState, resendSheet };
    const unsub = mp.onCombatantsUpdate(sessionId, (read) => applyRead(ctx, read));
    const audit = rosterAuditor(ctx, mp, sessionId);
    const tick = setInterval(() => void audit('tick'), ROSTER_AUDIT_MS);
    const offReconnect = mp.onReconnect?.(() => void audit('reconnect'));
    return () => {
      unsub();
      audit.stop();
      clearInterval(tick);
      offReconnect?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, mp, authoritative, isGm]);
}

/** Clean up pending writes on unmount. The refs never change, so this runs once. */
function useTimerCleanup({ stateTimers, sheetTimers }: SyncRefs) {
  useEffect(
    () => () => {
      Object.values(stateTimers.current).forEach(clearTimeout);
      Object.values(sheetTimers.current).forEach(clearTimeout);
    },
    [stateTimers, sheetTimers],
  );
}

/**
 * Firestore sync for the unified roster - see the file header.
 *
 * `hydrated` answers "has this client adopted the SHARED roster yet?". A
 * joining player boots with their own placeholder, carrying the same
 * `tab-1` id the GM's does; pushing it before the shared roster arrives
 * writes the blank over the GM's real combatant. So NOBODY writes until
 * their first snapshot is seen, and the decision is made on what the
 * collection CONTAINS, never on who you are - keying it off `isGm` let a
 * GM's second tab bootstrap its blank over the first (v7.4.1).
 */
export default function useCombatantSync(params: Params) {
  const { sessionId, isGm, roster } = params;
  const echo = useEchoPrevention();
  const refs = useSyncRefs(roster);
  const [hydrated, setHydrated] = useState(false);
  const syncCombatantState = useStateWriter(params, echo, refs);
  const syncCombatantSheet = useSheetWriter(params, echo, refs);
  const syncAllCombatants = useBatchWriter(params, echo);
  const removeCombatant = useRemoveCombatant(params, echo, refs);
  // The departure pass says who is on the roster now: a deletion heard about one of THEM was never
  // asked about (see `noteHeard`) and must not make a later, real removal look heard.
  const { heardGoneRef } = refs;
  const forgetHeard = useCallback(
    (present: readonly string[]) => present.forEach((id) => heardGoneRef.current.delete(id)),
    [heardGoneRef],
  );
  useSessionReset(sessionId, isGm, echo, refs, setHydrated);
  useRosterListener(params, echo, refs, setHydrated, syncCombatantState);
  useTimerCleanup(refs);
  return {
    syncCombatantState,
    syncCombatantSheet,
    syncAllCombatants,
    removeCombatant,
    forgetHeard,
    hydrated,
    echo,
    roster,
  };
}
